if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();
      this.points = new Map();
      this.scale = 1;
      this.offsetX = this.offsetY = 0;
      this.viewer = this.querySelector('[role="document"]');
      this.controls = document.createElement('div');
      this.controls.className = 'product-image-controls';
      this.controls.setAttribute('role', 'group');
      this.controls.setAttribute('aria-label', 'Image zoom controls');
      this.controls.innerHTML = '<span>Pinch or scroll to zoom · Drag to move</span><div><button type="button" data-zoom="out" aria-label="Zoom out">−</button><button type="button" data-zoom="reset" aria-label="Reset image zoom">Reset</button><button type="button" data-zoom="in" aria-label="Zoom in">+</button></div>';
      this.querySelector('[role="dialog"]').appendChild(this.controls);
      this.controls.addEventListener('pointerup', event => event.stopPropagation());
      this.controls.addEventListener('click', event => {
        const action = event.target.closest('button')?.dataset.zoom;
        if (action === 'reset') this.resetView();
        else if (action) this.zoomTo(this.scale * (action === 'in' ? 1.5 : 1 / 1.5));
      });
      this.viewer.addEventListener('pointerdown', event => {
        if (!this.mobileImage || (event.pointerType === 'mouse' && event.button !== 0)) return;
        event.preventDefault();
        this.points.set(event.pointerId, { x: event.clientX, y: event.clientY });
        this.viewer.setPointerCapture(event.pointerId);
        this.beginGesture();
      });
      this.viewer.addEventListener('pointermove', event => {
        if (!this.mobileImage || !this.points.has(event.pointerId)) return;
        event.preventDefault();
        this.points.set(event.pointerId, { x: event.clientX, y: event.clientY });
        const gesture = this.measureGesture();
        const ratio = gesture.distance && this.gesture.distance ? gesture.distance / this.gesture.distance : 1;
        this.scale = Math.max(1, Math.min(5, this.gesture.scale * ratio));
        const factor = this.scale / this.gesture.scale;
        this.offsetX = gesture.x - (this.gesture.x - this.gesture.offsetX) * factor;
        this.offsetY = gesture.y - (this.gesture.y - this.gesture.offsetY) * factor;
        this.renderView();
      });
      const endGesture = event => {
        if (!this.mobileImage) return;
        event.stopPropagation();
        this.points.delete(event.pointerId);
        this.beginGesture();
      };
      this.viewer.addEventListener('pointerup', endGesture);
      this.viewer.addEventListener('pointercancel', endGesture);
      this.viewer.addEventListener('lostpointercapture', endGesture);
      this.viewer.addEventListener('wheel', event => {
        if (!this.mobileImage) return;
        event.preventDefault();
        const rect = this.viewer.getBoundingClientRect();
        const x = event.clientX - rect.left - rect.width / 2;
        const y = event.clientY - rect.top - rect.height / 2;
        const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? rect.height : 1);
        const next = Math.max(1, Math.min(5, this.scale * Math.exp(-Math.max(-120, Math.min(120, delta)) * 0.005)));
        const factor = next / this.scale;
        this.offsetX = x - (x - this.offsetX) * factor;
        this.offsetY = y - (y - this.offsetY) * factor;
        this.scale = next;
        this.renderView();
      }, { passive: false });
      this.viewer.addEventListener('dragstart', event => { if (this.mobileImage) event.preventDefault(); });
      if (!document.getElementById('product-mobile-image-viewer-style')) {
        const style = document.createElement('style');
        style.id = 'product-mobile-image-viewer-style';
        style.textContent = 'html{scrollbar-gutter:stable}.product-image-controls{display:none}.product-media-modal.mobile-image-viewer .product-media-modal__dialog{height:100%;height:100dvh;position:relative;flex-direction:column;justify-content:center}.product-media-modal.mobile-image-viewer .product-media-modal__content{position:relative;flex:none;width:100%;height:calc(100% - 140px);max-height:none;padding:0;margin:60px 0 80px;overflow:hidden;touch-action:none;cursor:grab;user-select:none;-webkit-user-select:none}.product-media-modal.mobile-image-viewer .product-media-modal__content>img.active{position:absolute;left:50%;top:50%;max-width:none;max-height:none;min-width:0;margin:0;object-fit:contain;transform-origin:center;border:0;border-radius:0;will-change:transform}.product-media-modal.mobile-image-viewer .product-media-modal__content>:not(.active){display:none!important}.mobile-image-viewer .product-image-controls{display:flex;position:absolute;bottom:12px;bottom:max(12px,env(safe-area-inset-bottom));left:0;right:0;align-items:center;flex-direction:column;gap:5px;z-index:3;font-size:12px}.product-image-controls>div{display:flex;gap:12px}.product-image-controls button{min-width:44px;min-height:44px;border:1px solid currentColor;border-radius:8px;background:rgb(var(--color-background));color:rgb(var(--color-foreground));font:inherit;font-size:18px;cursor:pointer}.product-image-controls button[data-zoom=reset]{padding:0 16px;font-size:14px}.product-image-controls button:disabled{opacity:.4}.mobile-image-viewer .product-media-modal__toggle{top:max(12px,env(safe-area-inset-top));right:12px;min-width:44px;min-height:44px}';
        document.head.appendChild(style);
      }
    }

    hide() {
      this.stopMobileView();
      super.hide();
    }

    show(opener) {
      super.show(opener);
      this.showActiveMedia();
    }

    stopMobileView() {
      this.resizeObserver?.disconnect();
      this.points.clear();
      if (this.mobileImage) {
        this.mobileImage.removeEventListener('load', this.imageLoaded);
        this.mobileImage.style.width = '';
        this.mobileImage.style.height = '';
        this.mobileImage.style.transform = '';
      }
      this.mobileImage = null;
      this.classList.remove('mobile-image-viewer');
    }

    startMobileView(image) {
      this.mobileImage = image;
      this.classList.add('mobile-image-viewer');
      this.viewer.scrollTop = this.viewer.scrollLeft = 0;
      this.imageLoaded = () => this.fitImage();
      image.addEventListener('load', this.imageLoaded);
      this.resizeObserver = new ResizeObserver(() => this.fitImage());
      this.resizeObserver.observe(this.viewer);
      this.fitImage();
    }

    fitImage() {
      const image = this.mobileImage;
      if (!image || !image.naturalWidth || !this.viewer.clientHeight) return;
      const fit = Math.min((this.viewer.clientWidth - 24) / image.naturalWidth, (this.viewer.clientHeight - 24) / image.naturalHeight, 1);
      this.baseWidth = image.naturalWidth * fit;
      this.baseHeight = image.naturalHeight * fit;
      image.style.width = this.baseWidth + 'px';
      image.style.height = this.baseHeight + 'px';
      this.resetView();
    }

    resetView() {
      this.scale = 1;
      this.offsetX = this.offsetY = 0;
      this.renderView();
    }

    zoomTo(scale) {
      const next = Math.max(1, Math.min(5, scale));
      this.offsetX *= next / this.scale;
      this.offsetY *= next / this.scale;
      this.scale = next;
      this.renderView();
    }

    renderView() {
      if (!this.mobileImage) return;
      const limitX = Math.max(0, (this.baseWidth * this.scale - this.viewer.clientWidth) / 2);
      const limitY = Math.max(0, (this.baseHeight * this.scale - this.viewer.clientHeight) / 2);
      this.offsetX = Math.max(-limitX, Math.min(limitX, this.offsetX));
      this.offsetY = Math.max(-limitY, Math.min(limitY, this.offsetY));
      this.mobileImage.style.transform = 'translate(-50%, -50%) translate(' + this.offsetX + 'px,' + this.offsetY + 'px) scale(' + this.scale + ')';
      this.controls.querySelector('[data-zoom="out"]').disabled = this.scale <= 1;
      this.controls.querySelector('[data-zoom="in"]').disabled = this.scale >= 5;
    }

    measureGesture() {
      const points = Array.from(this.points.values()).slice(0, 2);
      const rect = this.viewer.getBoundingClientRect();
      const a = points[0];
      const b = points[1] || a;
      return { x: (a.x + b.x) / 2 - rect.left - rect.width / 2, y: (a.y + b.y) / 2 - rect.top - rect.height / 2, distance: points.length > 1 ? Math.hypot(a.x - b.x, a.y - b.y) : 0 };
    }

    beginGesture() {
      if (!this.points.size) return;
      this.gesture = { ...this.measureGesture(), scale: this.scale, offsetX: this.offsetX, offsetY: this.offsetY };
    }

    showActiveMedia() {
      this.stopMobileView();
      const mediaId = this.openedBy.getAttribute('data-media-id');
      this.querySelectorAll('[data-media-id]').forEach(element => element.classList.toggle('active', element.getAttribute('data-media-id') === mediaId));
      const activeMedia = this.querySelector('[data-media-id="' + mediaId + '"]');
      if (!activeMedia) return;
      if (activeMedia.tagName === 'IMG') {
        this.startMobileView(activeMedia);
        return;
      }
      const template = activeMedia.querySelector('template');
      activeMedia.scrollIntoView();
      this.viewer.scrollLeft = (activeMedia.width - this.viewer.clientWidth) / 2;
      if (activeMedia.nodeName === 'DEFERRED-MEDIA' && template?.content.querySelector('.js-youtube')) activeMedia.loadContent();
    }
  });
}
